import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { wsMessage } from '../environment/environmentSlice';
import { WsSubtitle } from '../../misc/domain/ws';

/**
 * Subtitle message received via WebSocket
 */
export interface SubtitleMessage {
  /** UUID of the interpreter sending the subtitle */
  interpreterId: string;
  /** Interpreter's display name */
  interpreterName: string;
  /** The transcribed text */
  text: string;
  /** Whether this is a final transcription */
  isFinal: boolean;
  /** Confidence score (0.0 - 1.0) */
  confidence?: number;
  /** Language code (e.g., 'el-GR') */
  language: string;
  /** Sequence number for ordering (per session) */
  sequence: number;
  /** Sender's transcription session id — resets the sequence when it changes */
  sessionId: string;
  /** Server timestamp */
  timestamp: number;
}

/**
 * Subtitle state for Redux store
 */
export interface SubtitlesState {
  /** Whether subtitles are enabled by the user */
  enabled: boolean;
  /** Whether the interpreter is currently transcribing (speaking) */
  isTranscribing: boolean;
  /** Current subtitle text to display */
  currentText: string;
  /** Whether the current text is a final result */
  isFinal: boolean;
  /** Sequence number of the latest message */
  sequence: number;
  /** Session id of the sender of the latest message */
  sessionId: string | null;
  /** Error message if any */
  error: string | null;
  /** Language code of the subtitles */
  language: string;
  /** Interpreter ID currently providing subtitles */
  interpreterId: string | null;
  /** Interpreter name for display */
  interpreterName: string | null;
  /** Timestamp when subtitles were last updated */
  lastUpdated: number | null;
}

const initialState: SubtitlesState = {
  enabled: true, // Enabled by default for users
  isTranscribing: false,
  currentText: '',
  isFinal: false,
  sequence: 0,
  sessionId: null,
  error: null,
  language: 'el-GR',
  interpreterId: null,
  interpreterName: null,
  lastUpdated: null,
};

/**
 * Common subtitle update data structure
 */
type SubtitleUpdateData = Omit<SubtitleMessage, 'confidence'>;

/**
 * Helper to apply subtitle update to state (used by both reducer and extraReducer)
 */
const applySubtitleUpdate = (
  state: SubtitlesState,
  data: SubtitleUpdateData
): void => {
  // Drop out-of-order messages, but only within the same sender session —
  // a new session id means the sender's counter restarted (page refresh,
  // rejoin, interpreter handoff) and its messages must not be discarded.
  if (data.sessionId === state.sessionId && data.sequence < state.sequence) {
    return;
  }

  state.currentText = data.text;
  state.isFinal = data.isFinal;
  state.sequence = data.sequence;
  state.sessionId = data.sessionId;
  state.isTranscribing = !data.isFinal;
  state.interpreterId = data.interpreterId;
  state.interpreterName = data.interpreterName;
  state.language = data.language;
  state.lastUpdated = data.timestamp;
  state.error = null;
};

const subtitles = createSlice({
  name: 'subtitles',
  initialState,
  reducers: {
    /**
     * Toggle subtitles on/off
     */
    toggleSubtitles(state) {
      state.enabled = !state.enabled;
    },

    /**
     * Enable subtitles
     */
    enableSubtitles(state) {
      state.enabled = true;
    },

    /**
     * Disable subtitles
     */
    disableSubtitles(state) {
      state.enabled = false;
    },

    /**
     * Handle incoming subtitle message from WebSocket
     */
    receiveSubtitle(state, action: PayloadAction<SubtitleMessage>) {
      applySubtitleUpdate(state, action.payload);
    },

    /**
     * Set transcription started
     */
    startTranscribing(state) {
      state.isTranscribing = true;
      state.error = null;
    },

    /**
     * Set transcription stopped
     */
    stopTranscribing(state) {
      state.isTranscribing = false;
    },

    /**
     * Clear current subtitle text
     */
    clearSubtitle(state) {
      state.currentText = '';
      state.isFinal = false;
      state.isTranscribing = false;
    },

    /**
     * Set error state
     */
    setSubtitleError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isTranscribing = false;
    },

    /**
     * Clear error state
     */
    clearSubtitleError(state) {
      state.error = null;
    },

    /**
     * Reset subtitles state (e.g., when leaving a room)
     */
    resetSubtitles(state) {
      state.currentText = '';
      state.isFinal = false;
      state.isTranscribing = false;
      state.sequence = 0;
      state.sessionId = null;
      state.error = null;
      state.interpreterId = null;
      state.interpreterName = null;
      state.lastUpdated = null;
      // Keep enabled preference
    },
  },
  extraReducers: (builder) => {
    builder.addCase(wsMessage, (state, { payload: msg }) => {
      if (msg.payload?.type === 'WsSubtitle') {
        const subtitle = msg.payload as WsSubtitle;
        // Map snake_case WebSocket format to camelCase
        applySubtitleUpdate(state, {
          text: subtitle.text,
          isFinal: subtitle.is_final,
          sequence: subtitle.sequence,
          sessionId: subtitle.session_id,
          interpreterId: subtitle.interpreter_id,
          interpreterName: subtitle.interpreter_name,
          language: subtitle.language,
          timestamp: subtitle.timestamp,
        });
      }
    });
  },
});

// Selectors
export const selectSubtitlesEnabled = (state: RootState) => state.subtitles.enabled;
export const selectCurrentSubtitle = (state: RootState) => state.subtitles.currentText;
export const selectIsTranscribing = (state: RootState) => state.subtitles.isTranscribing;
export const selectSubtitleError = (state: RootState) => state.subtitles.error;
export const selectIsFinal = (state: RootState) => state.subtitles.isFinal;
export const selectInterpreterName = (state: RootState) => state.subtitles.interpreterName;

// Actions
export const {
  toggleSubtitles,
  enableSubtitles,
  disableSubtitles,
  receiveSubtitle,
  startTranscribing,
  stopTranscribing,
  clearSubtitle,
  setSubtitleError,
  clearSubtitleError,
  resetSubtitles,
} = subtitles.actions;

// Reducer
export default subtitles.reducer;
